import { Alert, Button, TextField, Toggle } from '@warp-ds/react';
import cn from 'classnames';
import type { FormEvent } from 'react';
import { t } from '../../locale/texts';
import { getOneYearFromToday } from '../../util/datetime-fns';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { CustomSelect } from '../common/CustomSelect';
import { FrontpageDatePickerWrapper } from '../common/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import type { AutocompleteOption, AutocompleteResponse, MonthDuration, MonthPickerMonth, PrepackageSearchState } from '../types';
import { dateSelectionOptions } from './data/dateSelectionOptions';
import { PrepackageSearchOriginOptions } from './data/PrepackageSearchOriginOptions';
import { FlexibleDateSelection } from './FlexibleDateSelection';
import { PaxCombination } from './PaxCombination';
import { PrepackageHiddenInputs } from './PrepackageHiddenInputs';
import styles from './PrepackageSearch.module.css';

export type PrepackageAutoCompleteData = {
    ppAutocompleteUrl: string;
    autocompleteMapper: (options: AutocompleteResponse) => AutocompleteOption[];
    onDestinationSelect: (option: AutocompleteOption | null, typedText?: string) => void;
};

export type PrepackageSearchFormProps = {
    state: PrepackageSearchState;
    ready: boolean;
    ppResultUrl?: string;
    invalid: Record<string, boolean>;
    validationErrors: Partial<Record<'destination' | 'departureDate' | 'returnDate', string | null>>;
    hasNoOffers?: boolean;
    paxEnabled: boolean;
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
    onSearchTypeChange: (searchType: string) => void;
    onOriginAirportChange: (originAirport: string) => void;
    onToggleTravelMonth: (month: MonthPickerMonth) => void;
    onDurationChange: (duration: MonthDuration) => void;
    onDatesChange: (dates: { startDate: Date | null; endDate: Date | null }) => void;
    autoCompleteData?: PrepackageAutoCompleteData;
    onPaxChange: (adults: number, childrenAges: number[]) => void;
    compactTop?: boolean;
};

export const PrepackageSearchForm = ({
    state,
    ready,
    ppResultUrl,
    invalid,
    validationErrors,
    hasNoOffers,
    paxEnabled,
    onSubmit,
    onSearchTypeChange,
    onOriginAirportChange,
    onToggleTravelMonth,
    onDurationChange,
    onDatesChange,
    autoCompleteData,
    onPaxChange,
    compactTop = false,
}: PrepackageSearchFormProps) => {
    const anchorHelpText = Array.from(new Set([validationErrors.departureDate, validationErrors.returnDate].filter(Boolean))).join('. ');

    return (
        <SearchPanelWrapper onSubmit={onSubmit} action={ppResultUrl} method="GET" dataTestId="prepackageSearch" compactTop={compactTop}>
            <div className={styles['form-header']}>
                <Toggle
                    type="radio"
                    options={dateSelectionOptions}
                    selected={ready && state.searchType ? dateSelectionOptions.filter((opt) => opt.value === state.searchType) : []}
                    onChange={(selected) => onSearchTypeChange(selected.value)}
                    className="flex flex-row gap-8"
                />
            </div>
            <div className={styles['form-body']}>
                <div className={styles['form-body__row']}>
                    <CustomSelect
                        relative
                        fullWidth
                        fullscreenTitle="Hvor vil du reise fra?"
                        triggerLabel={t('prepackage.origin.label')}
                        options={PrepackageSearchOriginOptions}
                        value={state.originAirport}
                        onChange={onOriginAirportChange}
                    />
                    {autoCompleteData && (
                        <Autocomplete
                            vertical="prepackage"
                            id="destination"
                            label={t('prepackage.destination.label')}
                            placeholder={t('prepackage.destination.placeholder')}
                            defaultValue={state.destination}
                            url={autoCompleteData.ppAutocompleteUrl}
                            mapOptions={autoCompleteData.autocompleteMapper}
                            error={validationErrors.destination || undefined}
                            departure={false}
                            showDefaultOptionOnFocus
                            onSelect={autoCompleteData.onDestinationSelect}
                        />
                    )}
                </div>
                {state.searchType === 'flexible' ? (
                    <div className={styles['form-body__flexible-dates']}>
                        <FlexibleDateSelection
                            travelMonths={state.travelMonths}
                            duration={state.duration}
                            toggleMonth={onToggleTravelMonth}
                            setDuration={onDurationChange}
                        />
                    </div>
                ) : (
                    <FrontpageDatePickerWrapper
                        upperDateLimit={getOneYearFromToday()}
                        startDate={state.departureDate}
                        endDate={state.returnDate}
                        useRange
                        onDateChange={onDatesChange}
                        useAnchor={{
                            invalid: invalid.departureDate || invalid.returnDate,
                            helpText: anchorHelpText || undefined,
                        }}>
                        <TextField
                            id="date-range-start"
                            label={t('prepackage.departureLabel')}
                            invalid={invalid.departureDate}
                            helpText={validationErrors.departureDate || undefined}
                        />
                        <TextField
                            id="date-range-end"
                            label={t('prepackage.returnLabel')}
                            invalid={invalid.returnDate}
                            helpText={validationErrors.returnDate || undefined}
                        />
                    </FrontpageDatePickerWrapper>
                )}
                {paxEnabled && (
                    <PaxCombination
                        numberOfAdults={state.numberOfAdults}
                        agesOfChildren={state.agesOfChildren}
                        label="Reisende"
                        updatePax={onPaxChange}
                        hideLabelOnSmallScreen={true}
                    />
                )}

                <Button
                    className={cn(styles['search-button'], {
                        [styles['search-button--error']]: invalid && Object.values(invalid).some((v) => v),
                    })}
                    fullWidth
                    type="submit"
                    primary
                    data-testid="prepackageSearchButtonNew">
                    {t('action.search')}
                </Button>
            </div>
            <PrepackageHiddenInputs
                originAirport={state.originAirport}
                destinationId={state.destinationId}
                departureDate={state.departureDate}
                returnDate={state.returnDate}
                flexibleDeparture={state.flexibleDeparture}
                travelMonths={state.travelMonths}
                duration={state.duration}
                searchType={state.searchType}
                numberOfAdults={paxEnabled ? state.numberOfAdults : undefined}
                agesOfChildren={paxEnabled ? state.agesOfChildren : undefined}
            />
            <Alert type="info" show={hasNoOffers} data-testid="noOffersWarning" className="mt-8">
                {t('prepackage.validation.noOffers')}
            </Alert>
        </SearchPanelWrapper>
    );
};
